/**
 * Theme utilities for dynamic primary color support
 */

/**
 * Convert HSL to RGB values
 * @param h Hue (0-360)
 * @param s Saturation (0-100)
 * @param l Lightness (0-100)
 * @returns RGB values as [r, g, b]
 */
export function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  h /= 360;
  s /= 100;
  l /= 100;

  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h * 12) % 12;
    return l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
  };

  return [
    Math.round(f(0) * 255),
    Math.round(f(8) * 255),
    Math.round(f(4) * 255)
  ];
}

/**
 * Parse HSL string from CSS variable
 * @param hslString CSS HSL string like "221.2 83.2% 53.3%"
 * @returns RGB values as [r, g, b]
 */
export function parseHslString(hslString: string): [number, number, number] {
  const parts = hslString.trim().split(/\s+/);
  if (parts.length !== 3) {
    console.warn('Invalid HSL string format:', hslString);
    return [59, 130, 246]; // Fallback to blue
  }

  const h = parseFloat(parts[0]);
  const s = parseFloat(parts[1].replace('%', ''));
  const l = parseFloat(parts[2].replace('%', ''));

  return hslToRgb(h, s, l);
}

/**
 * Convert hex color to RGB values
 * @param hex Hex color string like "#ff0000" or "ff0000"
 * @returns RGB values as [r, g, b]
 */
export function hexToRgb(hex: string): [number, number, number] {
  hex = hex.replace(/^#/, '');
  if (hex.length === 3) {
    hex = hex.split('').map(x => x + x).join('');
  }
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return [r, g, b];
}

/**
 * Get css variable value from the DOM.
 * @param cssVar 
 * @returns 
 */
export function cssVarToHex(cssVar: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
}

/**
 * Get rgb string for a given hex value
 * @param hex 
 * @returns 
 */
export function hexToRgbString(hex: string): string {
  hex = hex.replace(/^#/, '');
  let bigint = parseInt(hex, 16);
  let r, g, b;
  if (hex.length === 6) {
    r = (bigint >> 16) & 255;
    g = (bigint >> 8) & 255;
    b = bigint & 255;
  } else if (hex.length === 3) {
    r = ((bigint >> 8) & 15) * 17;
    g = ((bigint >> 4) & 15) * 17;
    b = (bigint & 15) * 17;
  } else {
    return '0, 0, 0';
  }
  return `${r}, ${g}, ${b}`;
}

/**
 * Get contrasty color - i.e. white on red
 * @param hexcolor 
 * @returns 
 */
export function getContrastYIQ(hexcolor: string): string {
  hexcolor = hexcolor.replace(/^#/, '');
  if (hexcolor.length === 3) {
    hexcolor = hexcolor.split('').map(x => x + x).join('');
  }
  const r = parseInt(hexcolor.substring(0, 2), 16);
  const g = parseInt(hexcolor.substring(2, 4), 16);
  const b = parseInt(hexcolor.substring(4, 6), 16);
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return (yiq >= 128) ? '0, 0, 0' : '255, 255, 255';
}

// Helper: convert rgb string to hex
export function rgbToHex(rgbString: string): string {
  // Handle rgb(r, g, b) or rgba(r, g, b, a) format
  const match = rgbString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) {
    return '#000000'; // Default to black if parsing fails
  }

  const r = parseInt(match[1]);
  const g = parseInt(match[2]);
  const b = parseInt(match[3]);

  // Convert to hex and pad with zeros if needed
  const toHex = (n: number) => {
    const hex = n.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Get the appropriate text color (white or black) based on background color contrast.
 * This function can be used in React components to dynamically set text colors.
 * @param backgroundColor - The background color in hex, rgb, or hsl format
 * @returns The contrasting text color as 'rgb(r, g, b)' string
 */
export function getContrastTextColor(backgroundColor: string): string {
  try {
    let hexColor: string;

    // Check if it's already in RGB format
    const rgbMatch = backgroundColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbMatch) {
      const rgb = [
        parseInt(rgbMatch[1]),
        parseInt(rgbMatch[2]),
        parseInt(rgbMatch[3])
      ];
      hexColor = rgbToHex(`rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`);
    }
    // Check if it's HSL format
    else if (backgroundColor.includes('%') || backgroundColor.match(/^\d+\.?\d*\s+\d+\.?\d*%/)) {
      const rgb = parseHslString(backgroundColor);
      hexColor = rgbToHex(`rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`);
    }
    // Assume it's hex format
    else {
      hexColor = backgroundColor.startsWith('#') ? backgroundColor : `#${backgroundColor}`;
    }

    const contrastRgb = getContrastYIQ(hexColor);
    return `rgb(${contrastRgb})`;
  } catch (error) {
    console.error('Failed to calculate contrast text color:', error);
    return 'rgb(255, 255, 255)'; // Default to white
  }
}

/**
 * Initialize dynamic primary RGB support by reading --primary CSS variable
 * and setting --primary-rgb for use with rgba() opacity modifiers.
 * Also sets --primary-foreground to ensure proper text contrast.
 * Supports hex, HSL, and RGB color formats.
 */
export function initializeDynamicPrimaryRgb(): void {
  try {
    const root = document.documentElement;
    const primaryValue = cssVarToHex('--primary');
    
    if (!primaryValue) {
      console.warn('--primary CSS variable not found, using default');
      root.style.setProperty('--primary-rgb', '59, 130, 246'); // Default blue
      root.style.setProperty('--primary-foreground', 'rgb(255, 255, 255)'); // White text
      root.style.setProperty('--on-primary', 'rgb(255, 255, 255)'); // Yardi framework compatibility
      return;
    }

    let rgb: [number, number, number];
    let hexColor: string;

    // Check if it's already in RGB format (rgb(r, g, b) or rgba(r, g, b, a))
    const rgbMatch = primaryValue.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbMatch) {
      rgb = [
        parseInt(rgbMatch[1]),
        parseInt(rgbMatch[2]),
        parseInt(rgbMatch[3])
      ];
      hexColor = rgbToHex(`rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`);
    }
    // Check if it's HSL format (hsl(h, s%, l%) or "h s% l%")
    else if (primaryValue.includes('%') || primaryValue.match(/^\d+\.?\d*\s+\d+\.?\d*%/)) {
      rgb = parseHslString(primaryValue);
      hexColor = rgbToHex(`rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`);
    }
    // Assume it's hex format
    else {
      rgb = hexToRgb(primaryValue);
      hexColor = primaryValue.startsWith('#') ? primaryValue : `#${primaryValue}`;
    }

    // Set the --primary-rgb variable in "r, g, b" format
    root.style.setProperty('--primary-rgb', `${rgb[0]}, ${rgb[1]}, ${rgb[2]}`);
    
    // Calculate and set the contrasting foreground color
    const foregroundRgb = getContrastYIQ(hexColor);
    const foregroundColor = `rgb(${foregroundRgb})`;
    
    // Set multiple CSS variables for compatibility with different frameworks
    root.style.setProperty('--primary-foreground', foregroundColor);
    root.style.setProperty('--on-primary', foregroundColor);  // Used by Yardi framework
    
    // Debug logging
    console.log('🎨 Theme Contrast Initialized:', {
      primary: hexColor,
      'primary-rgb': `${rgb[0]}, ${rgb[1]}, ${rgb[2]}`,
      'primary-foreground': foregroundColor,
      'on-primary': foregroundColor,
      'YIQ value': ((rgb[0] * 299) + (rgb[1] * 587) + (rgb[2] * 114)) / 1000,
      'Text color': foregroundRgb === '255, 255, 255' ? 'WHITE (for dark bg)' : 'BLACK (for light bg)'
    });
  } catch (error) {
    console.error('Failed to initialize dynamic primary RGB:', error);
    // Set a default fallback
    document.documentElement.style.setProperty('--primary-rgb', '59, 130, 246');
    document.documentElement.style.setProperty('--primary-foreground', 'rgb(255, 255, 255)');
    document.documentElement.style.setProperty('--on-primary', 'rgb(255, 255, 255)');
  }
}